import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import { Sparkles, Users, Package, Gift, MessageSquare, Store, Layers, BarChart3, FileClock, ShieldAlert, Wand2, CreditCard, Receipt, DollarSign, ScrollText, Boxes, Printer, Flag, Crown, ShoppingBag } from "lucide-react";
import AdminStats from "./AdminStats";
import AdminTemplates from "./AdminTemplates";
import AdminListings from "./AdminListings";
import AdminPacks from "./AdminPacks";
import AdminCodes from "./AdminCodes";
import AdminCreditPackages from "./AdminCreditPackages";
import AdminPayments from "./AdminPayments";
import AdminUsers from "./AdminUsers";
import AdminRequests from "./AdminRequests";
import AdminAuditLog from "./AdminAuditLog";
import AdminModeration from "./AdminModeration";
import AdminReports from "./AdminReports";
import AdminEnhancements from "./AdminEnhancements";
import AdminVenmo from "./AdminVenmo";
import AdminTos from "./AdminTos";
import AdminPortal from "./AdminPortal";
import AdminPrintsQuotes from "./AdminPrintsQuotes";
import AdminWhatnot from "./AdminWhatnot";
import AdminPrintsCatalog from "./AdminPrintsCatalog";
import AdminHouses from "./AdminHouses";

function SideLink({ to, icon: Icon, children, end }) {
  return (
    <NavLink
      end={end}
      to={to}
      className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"}`}
    >
      <Icon className="h-4 w-4" /> {children}
    </NavLink>
  );
}

/** Compact chip-shaped nav link used in the mobile sticky rail. Same routes,
 *  smaller footprint, and the horizontal-scroll row hosts them side by side.
 *  We auto-scroll the active chip into view so it never slips off-screen. */
function ChipLink({ to, icon: Icon, children, end }) {
  return (
    <NavLink
      end={end}
      to={to}
      ref={(el) => {
        // Ensure the active chip stays visible when the rail is horizontally
        // scrollable. `scrollIntoView` with `inline: 'center'` handles both
        // initial mount and route changes.
        if (el && el.classList.contains('is-active')) {
          try { el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' }); } catch { /* older browsers */ }
        }
      }}
      className={({ isActive }) => `${isActive ? 'is-active ' : ''}shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${isActive ? 'border-cyan-300/60 bg-cyan-400/10 text-cyan-200' : 'border-white/10 text-white/60 hover:text-white hover:border-white/25'}`}
    >
      <Icon className="h-3.5 w-3.5" /> {children}
    </NavLink>
  );
}

const ADMIN_TABS = [
  { to: "/admin", end: true, icon: BarChart3, label: "Overview" },
  { to: "/admin/templates", icon: Layers, label: "Templates" },
  { to: "/admin/listings", icon: Store, label: "Listings" },
  { to: "/admin/packs", icon: Package, label: "Packs" },
  { to: "/admin/credit-packages", icon: CreditCard, label: "Credit Packs" },
  { to: "/admin/payments", icon: Receipt, label: "Payments" },
  { to: "/admin/venmo", icon: DollarSign, label: "Venmo Top-Ups" },
  { to: "/admin/codes", icon: Gift, label: "Codes" },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/requests", icon: MessageSquare, label: "Requests" },
  { to: "/admin/enhancements", icon: Wand2, label: "Enhancements" },
  { to: "/admin/moderation", icon: ShieldAlert, label: "Moderation" },
  { to: "/admin/reports", icon: Flag, label: "Reports" },
  { to: "/admin/tos", icon: ScrollText, label: "ToS Records" },
  { to: "/admin/portal", icon: Boxes, label: "Portal · 3D" },
  { to: "/admin/prints", icon: Printer, label: "Print Quotes" },
  { to: "/admin/prints-catalog", icon: Boxes, label: "Print Catalog" },
  { to: "/admin/whatnot", icon: ShoppingBag, label: "Whatnot" },
  { to: "/admin/houses", icon: Crown, label: "Houses" },
  { to: "/admin/audit", icon: FileClock, label: "Audit Log" },
];

export default function AdminPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-10">
      <div className="flex items-center gap-2 mb-3 sm:mb-6">
        <Sparkles className="h-5 w-5 text-cyan-300" />
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300">Admin Console</div>
      </div>

      {/* Mobile: sticky horizontal chip-rail so the tabs travel with the
       *  viewport. Hidden at lg where the vertical sidebar takes over. */}
      <div
        className="lg:hidden sticky top-[3.75rem] z-30 -mx-4 px-4 py-2 mb-3 bg-[hsl(222_28%_5%)]/95 backdrop-blur border-b border-white/8"
        data-testid="admin-mobile-tabs"
      >
        <div className="flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          {ADMIN_TABS.map((t) => (
            <ChipLink key={t.to} to={t.to} end={t.end} icon={t.icon}>{t.label}</ChipLink>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="hidden lg:block hud-pill rounded-2xl p-3 h-fit sticky top-20">
          <nav className="flex flex-col gap-1">
            {ADMIN_TABS.map((t) => (
              <SideLink key={t.to} to={t.to} end={t.end} icon={t.icon}>{t.label}</SideLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0" data-testid="admin-content">
          <Routes>
            <Route index element={<AdminStats />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="listings" element={<AdminListings />} />
            <Route path="packs" element={<AdminPacks />} />
            <Route path="credit-packages" element={<AdminCreditPackages />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="venmo" element={<AdminVenmo />} />
            <Route path="codes" element={<AdminCodes />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="requests" element={<AdminRequests />} />
            <Route path="enhancements" element={<AdminEnhancements />} />
            <Route path="moderation" element={<AdminModeration />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="tos" element={<AdminTos />} />
            <Route path="portal" element={<AdminPortal />} />
            <Route path="prints" element={<AdminPrintsQuotes />} />
            <Route path="prints-catalog" element={<AdminPrintsCatalog />} />
            <Route path="whatnot" element={<AdminWhatnot />} />
            <Route path="houses" element={<AdminHouses />} />
            <Route path="audit" element={<AdminAuditLog />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
